/**
 * Pages/Panel/ReportesNomina/ReporteLayoutEmpleados.js
 *
 * "Layout de Empleados": el padrón de trabajadores del mes en el formato que se
 * sube al portal del contratante. El archivo es una DECLARACIÓN — "en estas
 * fechas este trabajador tiene timbre" — así que las fechas del renglón son el
 * lapso realmente timbrado, no la ventana de relación laboral.
 *
 * Un renglón POR TRAMO, no por RFC. Un periodo que falta por timbrar (o que
 * salió en ceros) abre un hueco real: si cae al final del mes el tramo cierra
 * antes, y si cae en medio ese mismo trabajador sale con dos renglones o los que
 * hagan falta. El backend ya manda los tramos resueltos.
 *
 * El archivo destino es plano a propósito (una hoja, 7 columnas, todo texto):
 * lo lee un portal, no una persona. Por eso aquí no hay banda de título ni
 * renglón de totales — cualquier fila de más rompe la carga.
 *
 * Dos de las siete columnas no existen en el ERP y se capturan en la pestaña
 * "Datos del layout": el RFC del cliente y la sucursal. El RFC del proveedor sí
 * sale del sistema: es el de la empresa, que es quien presta el servicio.
 *
 * En pantalla cada renglón se puede abrir para ver los recibos del rango: los
 * que sustentan el tramo y, en naranja, los que no están timbrados — que son
 * justamente los que abren los huecos.
 */

import { message, PageHeader, Select, Space, Spin, Table, Tabs } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  InputItem,
  RangePickerItem,
  SelectItem,
  TableItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import { headerStyle } from "../../../Components/ExcelEstilos";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// Los encabezados del archivo del cliente, literales. El DOBLE ESPACIO de
// "Fecha Fin  (dd/MM/yyyy)" está en el original y se reproduce tal cual: el
// portal cotea el encabezado, no la posición.
const HEADERS_LAYOUT = [
  "RFC Trabajador",
  "Nombre",
  "RFC Cliente",
  "Fecha Inicio (dd/MM/yyyy)",
  "Fecha Fin  (dd/MM/yyyy)",
  "Sucursal",
  "RFC Proveedor",
];

// Anchos del archivo original, redondeados.
const ANCHOS_LAYOUT = [17, 37, 16, 12, 12, 9, 17];

// Todas las celdas del layout van como TEXTO, incluidas las fechas: así es el
// archivo de origen (formato "@" en las siete columnas) y así lo espera el
// portal. Una fecha escrita como número saldría como serial de Excel.
const CELDA_LAYOUT = { alignment: { vertical: "center" } };
const celdaTexto = (valor) => ({
  v: valor === undefined || valor === null ? "" : String(valor),
  t: "s",
  s: CELDA_LAYOUT,
});

const ESTATUS_RECIBO = {
  timbrada: "Timbrada",
  calculada: "Calculada",
  modificada: "Modificada",
  historica: "Histórica",
  error_timbrado: "Error de timbrado",
  pendiente: "Pendiente",
  sin_timbre: "Sin timbre (en ceros)",
};

class ReporteLayoutEmpleados extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    rango: null,
    // Un renglón por TRAMO timbrado: varios pueden compartir empleadoId.
    renglones: [],
    // Los recibos van aparte y no colgados de cada renglón: un empleado con tres
    // tramos triplicaría el arreglo en el JSON, y los recibos son los mismos.
    recibosPorEmpleado: {},
    trabajadores: 0,
    // Tenían timbre en el rango pero ningún periodo ordinario timbrado que
    // declarar, así que no van ni en pantalla ni en el Excel.
    descartados: 0,
    // Los que se quedaron fuera porque su único recibo del rango vino SIN
    // percepciones (la histórica que se cargó en blanco). Van aparte de
    // `descartados` porque el motivo es otro: ahí falta timbrar, aquí el importe.
    enCeros: 0,
    objetoRegistrosPatronales: null,
    // Lo capturado en la pestaña "Datos del layout" al momento de consultar: es
    // lo que se escribe en el Excel, no viene de la base.
    datosLayout: {},
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: res.data.registrosPatronales.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarReporte(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];
    axios
      .post(
        "reportesNomina/reporteLayoutEmpleados",
        {
          empresaId: this.props.empresaId,
          // El rango se compara contra los días del periodo de la prenómina, que
          // son fechas-día: se manda como "YYYY-MM-DD" para que parsearFechaDia lo
          // ancle igual sin depender de la zona horaria del navegador.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          registrosPatronales: values.registrosPatronales || [],
          departamentos: values.departamentos || [],
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            empresa: res.data.empresa,
            rango: res.data.rango,
            renglones: res.data.renglones || [],
            recibosPorEmpleado: res.data.recibosPorEmpleado || {},
            trabajadores: res.data.trabajadores || 0,
            descartados: res.data.descartados || 0,
            enCeros: res.data.enCeros || 0,
            datosLayout: {
              rfcCliente: (values.rfcCliente || "").trim().toUpperCase(),
              sucursal: values.sucursal || "",
            },
          });
          if ((res.data.renglones || []).length === 0) {
            // No es lo mismo que "no hay timbres": puede haberlos y venir todos
            // de nóminas extraordinarias, que no declaran días.
            message.info(
              "No hay ningún periodo ordinario timbrado dentro del rango"
            );
          }
        } else if (res.data.success === 1) {
          // Tope de filas o filtro incompleto: el backend dice cómo acotar.
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  descargarExcel() {
    const { renglones, empresa, datosLayout, rango } = this.state;
    if (renglones.length === 0) return;

    // Una fila por TRAMO. Un RFC con un hueco en medio del mes aparece dos veces,
    // con el lapso timbrado de cada tramo: es lo que hay que declarar.
    const filas = [HEADERS_LAYOUT.map((h) => ({ v: h, t: "s", s: headerStyle }))];
    renglones.forEach((renglon) => {
      filas.push([
        celdaTexto(renglon.rfc),
        celdaTexto(renglon.nombreCompleto),
        celdaTexto(datosLayout.rfcCliente),
        celdaTexto(renglon.fechaInicio),
        celdaTexto(renglon.fechaFin),
        celdaTexto(datosLayout.sucursal),
        celdaTexto(empresa?.rfc || ""),
      ]);
    });

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!cols"] = ANCHOS_LAYOUT.map((wch) => ({ wch }));
    // El autofiltro sí se puede usar aquí: la advertencia de estilosExcelNomina
    // es que se traga las filas de Totales, y este archivo no tiene ninguna.
    hoja["!autofilter"] = { ref: `A1:G${renglones.length + 1}` };

    const libro = XLSXStyle.utils.book_new();
    // La hoja del archivo original se llama "Sheet1"; se conserva el nombre.
    XLSXStyle.utils.book_append_sheet(libro, hoja, "Sheet1");
    const mes = moment(rango?.fechaInicio, "DD/MM/YYYY");
    XLSXStyle.writeFile(
      libro,
      `Layout_Empleados_${(mes.isValid() ? mes : moment()).format("MMM_YYYY").toUpperCase()}.xlsx`
    );
  }

  // Los recibos del rango. Van TODOS, no sólo los del tramo ni sólo los que
  // declaran días: el que NO tiene timbre es justamente el que explica por qué el
  // tramo cerró donde cerró, y se pinta en naranja para que salte a la vista.
  // Las históricas CON IMPORTE cuentan como timbradas, así que salen sin marcar y
  // sin UUID; la que viene en ceros sí sale marcada, porque no ampara nada.
  filaExpandida(renglon) {
    const recibos = this.state.recibosPorEmpleado[renglon.empleadoId] || [];

    const columnas = [
      {
        title: "Tramo",
        dataIndex: "tramo",
        key: "tramo",
        align: "center",
        width: 70,
        // Un recibo sin tramo no sustenta ninguna fecha del archivo: o no está
        // timbrado, o viene de una nómina que no declara días.
        render: (valor) => valor || "—",
      },
      { title: "Periodo", dataIndex: "prenomina", key: "prenomina", width: 240 },
      { title: "Inicio", dataIndex: "fechaInicio", key: "fechaInicio", align: "center", width: 100 },
      { title: "Fin", dataIndex: "fechaFin", key: "fechaFin", align: "center", width: 100 },
      { title: "Fecha de pago", dataIndex: "fechaPago", key: "fechaPago", align: "center", width: 110 },
      {
        title: "Tipo",
        dataIndex: "tipoNomina",
        key: "tipoNomina",
        width: 190,
        // Sin esta nota, una nómina ORDINARIA y TIMBRADA que sale con tramo "—"
        // no se explica. Pasa cuando trae conceptos restringidos: calcula sólo
        // esos conceptos, así que no paga los días del periodo y no declara nada.
        // Va por `cubreDias` y no por el estatus: al que sólo le falta timbrarse
        // sí le corresponden esos días, y ése ya se ve en naranja.
        render: (valor, recibo) =>
          recibo.cubreDias ? valor : `${valor} (no declara días)`,
      },
      {
        title: "Estatus",
        dataIndex: "estatus",
        key: "estatus",
        width: 170,
        // Un recibo sin percepciones no ampara nada aunque su estatus diga que se
        // timbró, así que el backend lo deja fuera del tramo y llega en naranja.
        // Sin esta nota el renglón no se explica: el naranja se lee como "falta
        // timbrar" y una "Histórica" ya se timbró en el sistema anterior — lo que
        // le falta es el importe. `sin_timbre` se salta porque su propia etiqueta
        // ya lo dice.
        render: (valor, recibo) => {
          const texto = ESTATUS_RECIBO[valor] || valor || "—";
          return recibo.enCeros && valor !== "sin_timbre"
            ? `${texto} (en ceros)`
            : texto;
        },
      },
      {
        title: "Días",
        dataIndex: "dias",
        key: "dias",
        align: "right",
        width: 70,
      },
      {
        title: "Neto",
        dataIndex: "neto",
        key: "neto",
        align: "right",
        width: 120,
        render: (valor) => dineroDisabledMask(valor || 0),
      },
      {
        title: "UUID",
        dataIndex: "uuid",
        key: "uuid",
        width: 300,
        render: (valor) => valor || "—",
      },
    ];

    return (
      <>
        <p style={{ margin: "0 0 6px", color: "#666" }}>
          {renglon.totalTramos > 1
            ? `Tramo ${renglon.tramo} de ${renglon.totalTramos}: `
            : "Lapso timbrado: "}
          <b>
            {renglon.fechaInicio} — {renglon.fechaFin}
          </b>
          {` · Alta ${renglon.fechaAlta || "—"} · Baja ${renglon.fechaBaja || "—"}`}
        </p>
        <Table
          size="small"
          rowKey={(recibo) => recibo.lineaId}
          columns={columnas}
          dataSource={recibos}
          pagination={false}
          scroll={{ x: "max-content" }}
          // Ésta sí es una Table de antd directa, así que rowClassName funciona
          // nativo. La clase `revisar` ya existe en App.css y cubre `> td` y
          // `:hover > td`, que es lo que hace falta para que antd no la pise.
          //
          // Va por `conTimbre` y no comparando el estatus aquí: la histórica con
          // importe cuenta como timbrada y no debe salir marcada, la que viene en
          // ceros sí, y esas dos reglas viven sólo en el backend.
          rowClassName={(recibo) => (recibo.conTimbre ? "" : "revisar")}
        />
      </>
    );
  }

  render() {
    const { loading, consultado, renglones } = this.state;

    // TableItem fuerza tableLayout fixed e ignora `scroll`, así que en pantalla
    // van sólo las columnas indispensables; el detalle va en la fila expandida.
    const columns = [
      { title: "RFC", dataIndex: "rfc", key: "rfc" },
      { title: "Nombre", dataIndex: "nombreCompleto", key: "nombreCompleto" },
      {
        title: "Departamento",
        dataIndex: "departamento",
        key: "departamento",
        render: (valor) => valor || "—",
      },
      {
        title: "Reg. Patronal",
        dataIndex: "rp",
        key: "rp",
        render: (valor) => valor || "—",
      },
      // Sólo dice algo cuando el RFC se partió: con un tramo el "1/1" es ruido en
      // todos los renglones del archivo normal.
      {
        title: "Tramo",
        dataIndex: "tramo",
        key: "tramo",
        align: "center",
        render: (valor, registro) =>
          registro.totalTramos > 1 ? `${valor}/${registro.totalTramos}` : "—",
      },
      {
        title: "Fecha Inicio",
        dataIndex: "fechaInicio",
        key: "fechaInicio",
        align: "center",
      },
      {
        title: "Fecha Fin",
        dataIndex: "fechaFin",
        key: "fechaFin",
        align: "center",
      },
      {
        // Los recibos timbrados que sustentan ESTE tramo, no todos los del
        // empleado: es el número que respalda las dos fechas del renglón.
        title: "Recibos",
        dataIndex: "recibosTramo",
        key: "recibosTramo",
        align: "right",
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteLayoutEmpleados"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
            sucursal: "Todas",
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Layout de Empleados"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {renglones.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Datos del layout">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    {/* Sin `required`: si se deja vacío, la columna C del Excel
                        sale vacía. Hay portales que no piden el RFC del
                        contratante y capturarlo a fuerza no aportaba nada. */}
                    <FormItem name="rfcCliente" label="RFC Cliente" margin>
                      <InputItem placeholder="Opcional" style={{ width: 220 }} />
                    </FormItem>
                    <FormItem name="sucursal" label="Sucursal" margin>
                      <InputItem placeholder="Todas" style={{ width: 160 }} />
                    </FormItem>
                  </div>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#666" }}>
                    El RFC del proveedor se toma de la empresa; estas dos columnas no
                    existen en el sistema y por eso se capturan aquí.
                  </p>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {this.state.trabajadores} trabajador(es) con periodo timbrado
                  {this.state.rango
                    ? ` del ${this.state.rango.fechaInicio} al ${this.state.rango.fechaFin}`
                    : ""}
                  {renglones.length !== this.state.trabajadores
                    ? `, ${renglones.length} renglón(es)`
                    : ""}
                  . Abre un renglón para ver los recibos que lo sustentan.
                  {this.state.descartados > 0 && (
                    // Un dato, no un renglón: no reintroduce a nadie al archivo,
                    // pero evita que faltar de timbrar pase inadvertido.
                    <span style={{ color: "#FA8C16" }}>
                      {` Se omitieron ${this.state.descartados} trabajador(es) con timbre en el rango pero sin ningún periodo ordinario timbrado que declarar.`}
                    </span>
                  )}
                  {this.state.enCeros > 0 && (
                    // Aviso propio y no sumado al de arriba: éstos se caen antes de
                    // hidratarse y por un motivo distinto —su recibo no tiene
                    // percepciones—, que casi siempre es una fila de la nómina
                    // histórica que se cargó vacía y hay que corregir en el origen.
                    <span style={{ color: "#FA8C16" }}>
                      {` ${this.state.enCeros} trabajador(es) quedaron fuera porque su recibo del rango vino en ceros (sin percepciones), y un recibo en ceros no ampara días.`}
                    </span>
                  )}
                </p>
                <TableItem
                  // El empleadoId ya no identifica un renglón: un RFC con un hueco
                  // en medio del mes aparece más de una vez.
                  rowKey={(registro) => registro.clave}
                  columns={columns}
                  dataSource={renglones}
                  size="small"
                  defaultPageSize={25}
                  expandable={{
                    expandedRowRender: (registro) => this.filaExpandida(registro),
                    rowExpandable: (registro) =>
                      (this.state.recibosPorEmpleado[registro.empleadoId] || []).length > 0,
                  }}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteLayoutEmpleados);
